import _ from 'underscore';
import { useIntl } from 'react-intl';
import { FormProvider, useForm } from 'react-hook-form';
import React, { useEffect, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, Settings2 } from 'lucide-react';

import Drawer, { DrawerHandle } from 'components/drawer';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { Accordion, AccordionSummary, Typography, AccordionDetails, Box, Button, CircularProgress, useTheme } from '@mui/material';
import ProductionCostSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/finance/production-cost-section';
import SalesPriceSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/finance/sales-price-section';
import TreatmentCostSection from 'views/bluviu-nutrition/features/scenarios-v2/components/scenario-setup-tab/finance/treatment-cost-section';

import { usePostProcessMapper } from 'views/bluviu-nutrition/features/scenarios-v2/hooks/use-post-process-mapper';
import { useScenarioResultState } from 'views/bluviu-nutrition/features/scenarios-v2/context/scenario-result-context';
import { useR2ScenarioState } from 'views/bluviu-nutrition/features/scenarios-v2/context/r2-scenario-context';
import { UpdateScenarioFinancialDataInput } from 'views/bluviu-nutrition/features/scenarios-v2/types/update-scenario-financial-data-input.type';

const ScenarioInputsDrawer: React.FC = () => {
  const intl = useIntl();
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();
  const [pricingExpanded, setIsPricingExpanded] = useState<boolean>(true);
  const [treatmentExpanded, setIsTreatmentExpanded] = useState<boolean>(true);
  const [productionCostExpanded, setIsProductionCostExpanded] = useState<boolean>(true);

  const hasReset = useRef(false);
  const drawerRef = useRef<DrawerHandle>(null);

  const { buildPostProcessBody } = usePostProcessMapper();
  const { updateScenarioPostProcessQuery } = useScenarioResultState();
  const {
    r2ScenarioQuery: { data: scenario },
  } = useR2ScenarioState();

  const form = useForm<UpdateScenarioFinancialDataInput>({
    defaultValues: {},
    mode: 'onChange',
    disabled: updateScenarioPostProcessQuery.isPending,
  });

  useEffect(() => {
    if (hasReset.current || !scenario?.financialData) return;
    const { currencyPerUnit, currency, scenarioId, treatmentsPerCage, costOfSingleTreatment, ...numericFields } =
      scenario.financialData;

    form.reset({
      treatmentsPerCage,
      costOfSingleTreatment,
      ..._.mapObject(numericFields, (v) => v || undefined),
    });

    hasReset.current = true;
  }, [scenario?.financialData]);

  const handleUpdate = form.handleSubmit((values) => {
    const body = buildPostProcessBody();
    if (!body || !scenario?.financialData) return;
    const { currency, currencyPerUnit } = scenario.financialData;

    updateScenarioPostProcessQuery.mutate(
      { ...body, financialData: { ...values, currency, currencyPerUnit } },
      {
        onSuccess: () => {
          form.reset(values);
          drawerRef.current?.close();
          snackbarDispatch({
            type: SnackbarActionTypes.SUCCESS_SNACKBAR,
            payload: { msg: intl.formatMessage({ id: 'snackbar.success.default' }) },
          });
        },
      }
    );
  });

  return (
    <FormProvider {...form}>
      <Drawer
        ref={drawerRef}
        title={'Adjust inputs'}
        anchor={'right'}
        onClose={() => form.reset()}
        actionButtons={
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={updateScenarioPostProcessQuery.isPending || !form.formState.isValid || !form.formState.isDirty || !scenario}>
            {updateScenarioPostProcessQuery.isPending ? <CircularProgress size={16} color="inherit" /> : 'Update'}
          </Button>
        }
        button={
          <Button
            variant="outlined"
            size="small"
            sx={{
              border: `1px solid ${theme.palette.primary.contrastText}`,
            }}
            startIcon={<Settings2 color={theme.palette.primary.contrastText} size={18} />}>
            <Typography variant="h6" fontWeight={600} color={theme.palette.primary.contrastText}>
              Inputs
            </Typography>
          </Button>
        }>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            textAlign: 'center',
            gap: 2,
            alignItems: 'center',
          }}>
          <Box display={'flex'} flexDirection={'column'} gap={4} width={700}>
            <Accordion
              expanded={treatmentExpanded}
              onChange={() => setIsTreatmentExpanded((prev) => !prev)}
              sx={{ background: 'transparent', p: 0, width: '100%' }}>
              <AccordionSummary aria-controls="panel1-content" id="panel2-header">
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  textAlign={'center'}
                  gap={2}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  width={'100%'}>
                  <Typography variant="h6" color={theme.palette.common.black}>
                    Treatments
                  </Typography>
                  {treatmentExpanded ? (
                    <ChevronUp color={theme.palette.common.black} />
                  ) : (
                    <ChevronDown color={theme.palette.common.black} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <TreatmentCostSection />
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box display={'flex'} flexDirection={'column'} gap={4} width={700}>
            <Accordion
              expanded={productionCostExpanded}
              onChange={() => setIsProductionCostExpanded((prev) => !prev)}
              sx={{ background: 'transparent', p: 0, width: '100%' }}>
              <AccordionSummary aria-controls="panel1-content" id="panel2-header">
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  textAlign={'center'}
                  gap={2}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  width={'100%'}>
                  <Typography variant="h6" color={theme.palette.common.black}>
                    Production costs
                  </Typography>
                  {productionCostExpanded ? (
                    <ChevronUp color={theme.palette.common.black} />
                  ) : (
                    <ChevronDown color={theme.palette.common.black} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <ProductionCostSection />
              </AccordionDetails>
            </Accordion>
          </Box>
          <Box display={'flex'} flexDirection={'column'} gap={4} width={700}>
            <Accordion
              expanded={pricingExpanded}
              onChange={() => setIsPricingExpanded((prev) => !prev)}
              sx={{ background: 'transparent', p: 0, width: '100%' }}>
              <AccordionSummary aria-controls="panel1-content" id="panel2-header">
                <Box
                  display={'flex'}
                  flexDirection={'row'}
                  textAlign={'center'}
                  gap={2}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  width={'100%'}>
                  <Typography variant="h6" color={theme.palette.common.black}>
                    Pricing
                  </Typography>
                  {pricingExpanded ? (
                    <ChevronUp color={theme.palette.common.black} />
                  ) : (
                    <ChevronDown color={theme.palette.common.black} />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <SalesPriceSection />
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Drawer>
    </FormProvider>
  );
};

export default React.memo(ScenarioInputsDrawer);
